import { useCallback, useEffect, useRef, useState } from "react";

import { useFolderById, useSetFolderNotes } from "@zapier/client";
import { useEmitFolderDocumentationInteractionEvent } from "@zapier/shared";

export type NotesSaveStatus = "idle" | "saving" | "saved" | "error_retrying";

const normalizeNotesFromApi = (notes: string | null | undefined) => notes ?? "";

interface UseFolderDocumentationNotesOptions {
  folderId: string;
}

const DEBOUNCE_MS = 500;
const MAX_WAIT_MS = 2000;
const SAVED_HIDE_MS = 3000;
const RETRY_INITIAL_MS = 2000;
const RETRY_MAX_MS = 30_000;

export function useFolderDocumentationNotes({
  folderId,
}: UseFolderDocumentationNotesOptions) {
  const { emit } = useEmitFolderDocumentationInteractionEvent();

  const { folder, isLoading } = useFolderById({
    folderId,
    enabled: true,
  });

  const { setFolderNotesAsync } = useSetFolderNotes({ folderId });

  const [notesDraft, setNotesDraft] = useState("");
  const notesDraftReference = useRef(notesDraft);
  const [notesSaveStatus, setNotesSaveStatus] =
    useState<NotesSaveStatus>("idle");
  const [hasHydratedInitialNotes, setHasHydratedInitialNotes] = useState(false);

  /*
    Notes flow (why refs exist):
    - Hydrate once from `useFolderById()` into `notesDraft` (unless the user already typed).
    - Render uses state (`notesDraft`, `notesSaveStatus`).
    - Autosave runs on timers/retries; refs hold the latest draft/status inputs so callbacks don't read
      a stale closure value and so we can avoid overlapping saves (only send the newest draft next).
  */
  const initialNotesReference = useRef<string | null | undefined>(undefined);
  const lastSavedNotesReference = useRef<string>("");
  const hasUserEditedReference = useRef(false);

  const hasEmittedStartTypingReference = useRef(false);
  const hasNonEmptyDraftReference = useRef(false);

  const debounceTimeoutReference = useRef<ReturnType<typeof setTimeout>>();
  const maxWaitTimeoutReference = useRef<ReturnType<typeof setTimeout>>();
  const savedHideTimeoutReference = useRef<ReturnType<typeof setTimeout>>();
  const retryTimeoutReference = useRef<ReturnType<typeof setTimeout>>();

  const inFlightReference = useRef(false);
  const pendingSaveReference = useRef(false);
  const retryDelayMsReference = useRef(RETRY_INITIAL_MS);

  const clearDebounceTimers = useCallback(() => {
    if (debounceTimeoutReference.current) {
      clearTimeout(debounceTimeoutReference.current);
      debounceTimeoutReference.current = undefined;
    }
    if (maxWaitTimeoutReference.current) {
      clearTimeout(maxWaitTimeoutReference.current);
      maxWaitTimeoutReference.current = undefined;
    }
  }, []);

  const clearSavedHideTimer = useCallback(() => {
    if (savedHideTimeoutReference.current) {
      clearTimeout(savedHideTimeoutReference.current);
      savedHideTimeoutReference.current = undefined;
    }
  }, []);

  const clearRetryTimer = useCallback(() => {
    if (retryTimeoutReference.current) {
      clearTimeout(retryTimeoutReference.current);
      retryTimeoutReference.current = undefined;
    }
  }, []);

  const emitStartTypingNotesEventOnce = useCallback(
    (nextDraft: string) => {
      if (hasEmittedStartTypingReference.current) {
        return;
      }
      if (initialNotesReference.current === undefined) {
        return;
      }
      const initialNotes = initialNotesReference.current ?? "";
      if (initialNotes.length > 0) {
        return;
      }

      if (nextDraft.trim().length === 0) {
        return;
      }

      hasEmittedStartTypingReference.current = true;
      emit({
        optionSelected: "start_typing_notes",
        folderId,
      });
    },
    [emit, folderId],
  );

  const saveNow = useCallback(
    async function saveNowImpl() {
      clearDebounceTimers();

      const nextDraft = notesDraftReference.current;
      const isDirty = nextDraft !== lastSavedNotesReference.current;
      if (!isDirty) {
        clearRetryTimer();
        return;
      }

      clearSavedHideTimer();

      if (inFlightReference.current) {
        pendingSaveReference.current = true;
        return;
      }

      inFlightReference.current = true;
      pendingSaveReference.current = false;
      setNotesSaveStatus("saving");
      clearRetryTimer();

      // Intentionally send explicit null to clear notes.
      // eslint-disable-next-line unicorn/no-null
      const notesToSave = nextDraft.length === 0 ? null : nextDraft;

      try {
        await setFolderNotesAsync({ folderId, notes: notesToSave });

        lastSavedNotesReference.current = nextDraft;
        retryDelayMsReference.current = RETRY_INITIAL_MS;

        setNotesSaveStatus("saved");
        savedHideTimeoutReference.current = setTimeout(() => {
          setNotesSaveStatus((current) =>
            current === "saved" ? "idle" : current,
          );
        }, SAVED_HIDE_MS);
      } catch {
        setNotesSaveStatus("error_retrying");

        retryTimeoutReference.current = setTimeout(() => {
          void saveNowImpl();
        }, retryDelayMsReference.current);

        retryDelayMsReference.current = Math.min(
          retryDelayMsReference.current * 2,
          RETRY_MAX_MS,
        );
      } finally {
        inFlightReference.current = false;

        if (pendingSaveReference.current) {
          pendingSaveReference.current = false;
          void saveNowImpl();
        }
      }
    },
    [
      clearDebounceTimers,
      clearRetryTimer,
      clearSavedHideTimer,
      setFolderNotesAsync,
      folderId,
    ],
  );

  // Hydrate from API once, without clobbering user edits.
  useEffect(() => {
    if (!folder) {
      return;
    }

    if (initialNotesReference.current === undefined) {
      initialNotesReference.current = folder.notes;
      const normalized = normalizeNotesFromApi(folder.notes);
      lastSavedNotesReference.current = normalized;
      if (!hasUserEditedReference.current) {
        setNotesDraft(normalized);
      }
      setHasHydratedInitialNotes(true);

      if (hasNonEmptyDraftReference.current) {
        emitStartTypingNotesEventOnce(notesDraftReference.current);
      }
    }
  }, [folder, emitStartTypingNotesEventOnce]);

  useEffect(() => {
    // Keep refs in sync for timer/async logic without re-rendering:
    // - refs don't trigger re-renders; state (`notesDraft`) does
    notesDraftReference.current = notesDraft;
    hasNonEmptyDraftReference.current = notesDraft.trim().length > 0;
  }, [notesDraft]);

  // Schedule saves while typing: trailing debounce + max-wait batching.
  useEffect(() => {
    const isDirty = notesDraft !== lastSavedNotesReference.current;
    if (!isDirty) {
      clearDebounceTimers();
      return;
    }

    if (notesSaveStatus === "saved") {
      clearSavedHideTimer();
      setNotesSaveStatus("idle");
    }

    if (debounceTimeoutReference.current) {
      clearTimeout(debounceTimeoutReference.current);
    }
    debounceTimeoutReference.current = setTimeout(() => {
      void saveNow();
    }, DEBOUNCE_MS);

    if (!maxWaitTimeoutReference.current) {
      maxWaitTimeoutReference.current = setTimeout(() => {
        void saveNow();
      }, MAX_WAIT_MS);
    }
  }, [
    clearDebounceTimers,
    clearSavedHideTimer,
    notesDraft,
    notesSaveStatus,
    saveNow,
  ]);

  useEffect(() => {
    return () => {
      clearDebounceTimers();
      clearSavedHideTimer();
      clearRetryTimer();
    };
  }, [clearDebounceTimers, clearRetryTimer, clearSavedHideTimer]);

  const handleChangeDraft = useCallback(
    (nextDraft: string) => {
      hasUserEditedReference.current = true;
      emitStartTypingNotesEventOnce(nextDraft);
      setNotesDraft(nextDraft);
    },
    [emitStartTypingNotesEventOnce],
  );

  const handleBlur = useCallback(() => {
    void saveNow();
  }, [saveNow]);

  return {
    notesDraft,
    notesSaveStatus,
    isInitialNotesLoading: isLoading && folder === undefined,
    hasHydratedInitialNotes,
    onChangeNotesDraft: handleChangeDraft,
    onBlurNotesDraft: handleBlur,
  };
}
